"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import "./overlays.css";

/** Past this, a navigation that never landed (a failed fetch, an aborted one) stops saying it is on its way. */
const GIVE_UP_MS = 20_000;

/** A click that will move the page within the app — not a new tab, a download, another site, or the same address. */
function internalTarget(e: MouseEvent): URL | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!a || a.hasAttribute("download") || a.target && a.target !== "_self") return null;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return null; // same page, or a #hash
  return url;
}
function Bar() {
  const $ = _c(12);
  const pathname = usePathname();
  const search = useSearchParams()?.toString() ?? "";
  const [state, setState] = useState("idle");
  const giveUp = useRef(null);
  const fade = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const onClick = e => {
        if (!internalTarget(e)) {
          return;
        }
        if (fade.current) {
          clearTimeout(fade.current);
        }
        if (giveUp.current) {
          clearTimeout(giveUp.current);
        }
        setState("loading");
        giveUp.current = setTimeout(() => setState("idle"), GIVE_UP_MS);
      };
      document.addEventListener("click", onClick, true);
      return () => document.removeEventListener("click", onClick, true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setState(_temp);
      if (giveUp.current) {
        clearTimeout(giveUp.current);
      }
      fade.current = setTimeout(() => setState(_temp2), 400);
      return () => {
        if (fade.current) {
          clearTimeout(fade.current);
        }
      };
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== pathname || $[4] !== search) {
    t3 = [pathname, search];
    $[3] = pathname;
    $[4] = search;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== state) {
    t4 = () => {
      if (state === "loading") {
        document.documentElement.dataset.navigating = "";
      } else {
        delete document.documentElement.dataset.navigating;
      }
    };
    t5 = [state];
    $[6] = state;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (state === "idle") {
    return null;
  }
  const t6 = state === "loading" || undefined;
  let t7;
  if ($[9] !== state || $[10] !== t6) {
    t7 = <div className="v2 v2-pop-portal"><div className="v2-nav-progress" data-state={state} role="progressbar" aria-busy={t6} /></div>;
    $[9] = state;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

/**
 * THE PAGE IS ON ITS WAY — a thin gilt line across the top of the screen from the click to the next page's arrival.
 * Detail pages (a collection, an NFT, an auction…) have no `loading.tsx` on purpose (a loading boundary there turns
 * a missing page's 404 into a 200), so until now nothing at all moved while the first visit was fetched. Mounted
 * once beside the shell; renders nothing while idle.
 */
function _temp2(s_0) {
  return s_0 === "done" ? "idle" : s_0;
}
function _temp(s) {
  return s === "loading" ? "done" : s;
}
export default function NavProgress() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><Bar /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}